/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { ClickableProps } from "@insite/mobius/Clickable";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import {
    Clickable,
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Typography,
} from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadProductPartNumbers from "@monsma/App/Handlers/Data/Products/LoadProductPartNumbers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    userName: state.context.session.userName,
    currentUserIsGuest: getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    loadProductPartNumbers,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductPartNumbersStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    tableWrapper?: InjectableCss;
    clickable?: ClickableProps;
    icon?: IconProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    wrapper: {
        css: css`
            margin-bottom: 40px;

            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    title: {
        variant: "h3",
        as: "h2",
        color: "secondary.main",
        css: css`
            margin-bottom: 20px;
        `,
    },
    tableWrapper: {
        css: css`
            display: none;

            &.is-open {
                display: block;
            }

            @media print {
                display: block;
            }
        `,
    },
    clickable: {
        css: css`
            font-size: 15px;
            line-height: 26px;
            font-weight: 600;
            color: ${getColor("common.ui2")};
            margin-bottom: 20px;

            &,
            > span {
                transition: all 0.2s linear;
            }

            &:hover {
                &,
                > span {
                    color: ${getColor("common.ui3")};
                }
            }
        `,
    },
    icon: {
        size: 22,
        color: "common.ui5",
        css: css`
            margin-left: 15px;
        `,
    },
};

const styles = productPartNumbersStyles;

const ProductPartNumbersWidget: React.FC<Props> = ({
    product,
    userName,
    currentUserIsGuest,
    loadProductPartNumbers,
}) => {
    const authorized = userName && !currentUserIsGuest;
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";

    if (!authorized && !isCmsEditing) {
        return null;
    }

    const [isOpen, setIsOpen] = useState(false);
    const [partNumbers, setPartNumbers] = useState([]);

    useEffect(() => {
        loadProductPartNumbers({
            id: product?.id,
            onSuccess: (result: any) => {
                setPartNumbers(result.partNumbers);
            },
            onError: (error: any) => {},
        });
    }, [product?.id]);

    if (!partNumbers || !partNumbers.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Part Numbers")}</Typography>
            <Clickable
                {...styles.clickable}
                onClick={() => {
                    setIsOpen(!isOpen);
                }}
            >
                {isOpen && (
                    <>
                        {translate("Hide Part Number Table")}
                        <IconMemo {...styles.icon} src={ChevronUp} />
                    </>
                )}
                {!isOpen && (
                    <>
                        {translate("Show Part Number Table")}
                        <IconMemo {...styles.icon} src={ChevronDown} />
                    </>
                )}
            </Clickable>
            <StyledWrapper className={`${isOpen && "is-open"}`} {...styles.tableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader>{translate("Number")}</DataTableHeader>
                        <DataTableHeader>{translate("Type")}</DataTableHeader>
                        <DataTableHeader>{translate("Source")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {partNumbers.map((productPartNumber: any, index) => {
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <DataTableRow key={`productPartNumbers_${index}`}>
                                    <DataTableCell>{productPartNumber.partNumber || "--"}</DataTableCell>
                                    <DataTableCell>{productPartNumber.type || "--"}</DataTableCell>
                                    <DataTableCell>{productPartNumber.source || "--"}</DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductPartNumbersWidget)),
    definition: {
        displayName: "Product Part Numbers",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
